/**
 * The checkout's shape while its data is on the way.
 *
 * The dialog is open and cannot draw a form yet, so it draws the form it is
 * about to be. That reads better than a spinner alone — the sheet arrives with
 * structure — and it keeps the first frame cheap, because the form itself is not
 * what the browser has to paint at the top of the slide.
 */

import type { ReactNode } from 'react';
import { Row, Stack } from '@yummy/ui-rnr';
import { Skeleton } from '@yummy/ui-rnr/components/ui/skeleton';

/** One section: the title line, then whatever the section would hold. */
function SectionSkeleton({ children }: { children: ReactNode }) {
  return (
    <Stack gap="md">
      <Skeleton className="h-5 w-40" />
      {children}
    </Stack>
  );
}

/**
 * Renders the checkout as grey blocks.
 *
 * @returns The placeholder, shaped like the order, the pickup time, the contact
 *   form, and the payment note.
 */
export function CheckoutSkeleton() {
  return (
    <Stack gap="xl">
      {/* Your order: two lines, each with a quantity control. */}
      <SectionSkeleton>
        <Stack gap="lg">
          {[0, 1].map((row) => (
            <Row key={row} spread gap="md" align="center">
              <Stack gap="xs" className="flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-24" />
              </Stack>
              <Skeleton className="h-8 w-24 rounded-lg" />
            </Row>
          ))}
        </Stack>
      </SectionSkeleton>

      {/* Pickup time: the two choices. */}
      <SectionSkeleton>
        <Stack gap="sm">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-14 w-full rounded-xl" />
        </Stack>
      </SectionSkeleton>

      {/* Your details: the labelled fields. */}
      <SectionSkeleton>
        <Stack gap="md">
          {[0, 1, 2].map((field) => (
            <Stack key={field} gap="xs">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-11 w-full rounded-md" />
            </Stack>
          ))}
        </Stack>
      </SectionSkeleton>

      {/* Payment note. */}
      <SectionSkeleton>
        <Skeleton className="h-16 w-full rounded-xl" />
      </SectionSkeleton>
    </Stack>
  );
}
